import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file for VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY');
}

// ── Sesión compartida con hub.easytek.cr ─────────────────────────────
// El hub escribe la sesión en una cookie de dominio .easytek.cr para que
// todas las herramientas internas compartan un solo login. El contrato
// completo (por qué el valor va URL-encoded, por qué expires_at importa)
// está documentado en el comentario de cabecera de src/session.ts del repo
// easytek-hub. Este adapter implementa ese contrato.
//
// Dos capas que no hay que confundir: la cookie TRANSPORTA el JSON
// URL-encoded; auth-js espera JSON plano. Por eso getItem decodifica y
// setItem codifica.
//
// Lectura con respaldo, a propósito: primero la cookie del hub, y si no hay,
// el localStorage que el ITSM viene usando. Así nadie queda afuera durante
// la transición: quien viene del hub hereda la sesión, quien ya tenía sesión
// abierta acá la conserva, y quien entra directo sigue pudiendo usar el
// login propio del ITSM (que además exige segundo factor). El localStorage
// se vacía solo: en cuanto la sesión se renueva, la nueva se escribe en la
// cookie.
// Partido en trozos cuando no cabe, a propósito: el navegador tira ~4KB por
// cookie y lo hace EN SILENCIO — no lanza, no avisa. Si la sesión crece (basta
// con que user_metadata engorde), la cookie no se guarda, nadie puede entrar y
// no hay error que mirar. Medido en itsm.easytek.cr: 3566 bytes se guardan,
// 4366 desaparecen. Por eso, si el valor no cabe entero, se reparte en
// `clave.0`, `clave.1`… (misma convención que supabase-ssr) y se reensambla al
// leer. Se escribe cookie ÚNICA mientras quepa: así el caso normal de hoy
// queda igual que antes y el hub, que lee esa cookie, no se entera del cambio;
// el troceado solo aparece donde la alternativa era perder la sesión completa.
const COOKIE_DOMAIN = '.easytek.cr';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
// Presupuesto conservador para `nombre=valor`: el límite real ronda 4096 y los
// navegadores no coinciden en si cuentan los atributos. Se deja margen.
const LIMITE_COOKIE = 3500;
const MAX_TROZOS = 20;

function atributos(maxAge) {
  return `Domain=${COOKIE_DOMAIN}; Path=/; Secure; SameSite=Lax; Max-Age=${maxAge}`;
}
function escribirCookie(nombre, valor, maxAge = COOKIE_MAX_AGE) {
  document.cookie = `${nombre}=${valor}; ${atributos(maxAge)}`;
}
function borrarCookie(nombre) {
  escribirCookie(nombre, '', 0);
}
function leerCookieCruda(nombre) {
  if (typeof document === 'undefined') return null;
  const par = document.cookie.split('; ').find((p) => p.startsWith(`${nombre}=`));
  return par ? par.slice(nombre.length + 1) : null;
}
// Junta `clave.0`, `clave.1`… hasta el primer hueco. Devuelve el valor crudo
// (todavía URL-encoded) o null si no hay ningún trozo.
function leerTrozos(clave) {
  let crudo = '';
  for (let i = 0; i < MAX_TROZOS; i += 1) {
    const trozo = leerCookieCruda(`${clave}.${i}`);
    if (trozo === null) break;
    crudo += trozo;
  }
  return crudo === '' ? null : crudo;
}
function borrarTrozos(clave) {
  for (let i = 0; i < MAX_TROZOS; i += 1) {
    if (leerCookieCruda(`${clave}.${i}`) === null) break;
    borrarCookie(`${clave}.${i}`);
  }
}

// Quita user.identities de la sesión antes de guardarla, igual que hace el hub
// (encodeSession / trimCookieUser en src/session.ts de easytek-hub). Falla del
// 2026-10-27: con identities la sesión real mide ~3650 bytes codificada, no
// cabe en LIMITE_COOKIE y se troceaba; el hub lee solo la cookie entera, así que
// cada renovación del token lo dejaba sin sesión y lo mandaba al login. Nada de
// las tres apps lee identities de la sesión guardada (la lista viva sale de
// Supabase). Un valor que no es una sesión JSON con user se devuelve igual.
function sinIdentities(value) {
  let s;
  try {
    s = JSON.parse(value);
  } catch {
    return value;
  }
  if (!s || typeof s !== 'object' || !s.user || typeof s.user !== 'object' || !('identities' in s.user)) return value;
  const { identities: _identities, ...user } = s.user;
  return JSON.stringify({ ...s, user });
}

// Exportado para poder testearlo: es la pieza de la que depende que alguien
// pueda entrar o no, y este repo no tiene otra cobertura.
export const cookieConRespaldoStorage = {
  getItem: (key) => {
    // Orden: cookie entera → cookie troceada → respaldo en localStorage.
    const raw = leerCookieCruda(key) ?? leerTrozos(key);
    if (raw !== null) {
      try {
        return decodeURIComponent(raw);
      } catch {
        // Cookie mal codificada: se ignora y se cae al respaldo en vez de
        // dejar a la persona sin sesión.
      }
    }
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    const codificado = encodeURIComponent(sinIdentities(value));
    if (key.length + 1 + codificado.length <= LIMITE_COOKIE) {
      escribirCookie(key, codificado);
      // Si venía troceada y ahora cabe entera, los trozos viejos sobran.
      borrarTrozos(key);
      return;
    }
    // No cabe: se trocea. La cookie base se BORRA — si quedara, getItem la
    // leería primero y devolvería la sesión vieja.
    const tamTrozo = Math.max(1, LIMITE_COOKIE - (key.length + 5));
    const trozos = [];
    for (let i = 0; i < codificado.length; i += tamTrozo) {
      trozos.push(codificado.slice(i, i + tamTrozo));
    }
    borrarCookie(key);
    borrarTrozos(key);
    trozos.slice(0, MAX_TROZOS).forEach((trozo, i) => escribirCookie(`${key}.${i}`, trozo));
  },
  removeItem: (key) => {
    // Cerrar sesión limpia las dos capas: si solo se borrara la cookie, el
    // respaldo en localStorage volvería a loguear a la persona sola.
    borrarCookie(key);
    borrarTrozos(key);
    try {
      window.localStorage.removeItem(key);
    } catch {
      // localStorage bloqueado (modo privado, políticas): la cookie ya se borró.
    }
  },
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Se mantiene en true, a diferencia del contrato de fase 2 que lo sugiere
    // en false. Razón: el equipo pasa horas dentro del ITSM sin volver al hub,
    // y si solo el hub renovara, la sesión vencería a la hora y los sacaría.
    // El riesgo de que dos apps roten el mismo refresh token existe, pero
    // Supabase tiene ventana de gracia para eso y desloguear cada hora es peor.
    autoRefreshToken: true,
    persistSession: true,
    // En false a propósito (antes true). Con true, auth-js lee
    // #access_token=...&refresh_token=... de la URL al arrancar y guarda esa sesión
    // con el adapter, o sea en la cookie compartida de .easytek.cr: un enlace armado
    // por un atacante metía SU sesión en el hub, el ITSM y el tablero (login CSRF /
    // fijación de sesión). easytekdb permite registrarse sin confirmar el correo, así
    // que cualquiera consigue tokens válidos para armarlo. Los únicos enlaces que
    // necesitan traer sesión son los de alta y recuperación de provision-client.ts
    // (easytek-ai); los atiende AccessLinkGate, que pide un clic mostrando de quién
    // es la cuenta antes de adoptarla. Test: supabase.url.test.js.
    detectSessionInUrl: false,
    storage: cookieConRespaldoStorage,
  }
});

// De quién es un access token, según Supabase, SIN tocar la sesión guardada. Lo usa
// AccessLinkGate para mostrar la cuenta antes del clic: el correo del payload del token no
// sirve, porque nadie lo valida (setSession, con un token vencido o sin exp, ni lo mira y
// usa solo el refresh token). No se usa supabase.auth.getUser(token) a propósito: si
// Supabase responde session_not_found, auth-js BORRA la sesión guardada, y un enlace armado
// podría desloguear a la persona. Devuelve null si Supabase no reconoce el token; sin red,
// lanza, para que la pantalla ofrezca reintentar.
export async function usuarioDelToken(token) {
  const r = await fetch(`${supabaseUrl}/auth/v1/user`, {
    headers: { apikey: supabaseAnonKey, Authorization: `Bearer ${token}` },
  });
  if (!r.ok) return null;
  const usuario = await r.json().catch(() => null);
  return usuario?.id && usuario?.email ? { id: usuario.id, email: usuario.email } : null;
}

// ── TEMPORARY DEBUG: catch all uncaught errors globally ──────────────
if (typeof window !== 'undefined') {
  window.__SUPABASE_DEBUG__ = true;
  window.addEventListener('unhandledrejection', (e) => {
    console.error('[GLOBAL DEBUG] Unhandled rejection:', e?.reason);
  });
  window.onerror = (msg, src, line, col, err) => {
    console.error('[GLOBAL DEBUG] Error:', msg, src, line, col, err);
  };
}
